import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, Radio, Activity, Flame, Users, Eye, FileText, MousePointerClick, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { adminLiveAudienceSnapshot, adminAudienceVisitors } from "../../lib/api";
import { VisitorDossierDrawer } from "./VisitorDossierDrawer";
import { ReachPanel } from "./ReachPanel";
import { getAdminIgnoreSelf, setAdminIgnoreSelf } from "../LiveAudienceTracker";

/**
 * LiveAudiencePanel — admin-only realtime telemetry.
 *
 * Renders four sections:
 *   1. LIVE NOW           — anonymous sessions active in last 5 min
 *   2. RECENT ACTIVITY    — 24h event feed (route changes, dossier opens, source clicks)
 *   3. DOSSIER HEAT       — top 25 dossiers by event count in last 24h
 *   4. LIVE SUBSCRIBERS   — known subscriber emails currently online (plaintext)
 *
 * Refreshes every 10 seconds.
 */

const REFRESH_INTERVAL_MS = 10_000;

const fmtAgo = (iso) => {
  if (!iso) return "—";
  try {
    const t = new Date(iso).getTime();
    const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (sec < 5) return "just now";
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
  } catch {
    return "—";
  }
};

const KindBadge = ({ kind }) => {
  const styles = {
    dossier_open:      "text-[#D92D20] border-[#D92D20]/40",
    page_view:         "text-[#A39E93] border-[#454038]",
    source_click:      "text-[#5DD4B3] border-[#5DD4B3]/40",
    newsletter_signup: "text-[#CBB892] border-[#CBB892]/40",
    newsletter_click:  "text-[#9FA8D4] border-[#9FA8D4]/40",
  };
  const cls = styles[kind] || "text-[#757065] border-[#454038]";
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 border font-mono text-[9px] uppercase tracking-[0.25em] ${cls}`}>
      {(kind || "").replace(/_/g, " ")}
    </span>
  );
};

const DeviceTag = ({ device }) => {
  if (!device) return null;
  return (
    <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">
      {device}
    </span>
  );
};

const SectionShell = ({ icon: Icon, label, count, testid, children }) => (
  <section className="border border-[#2D2A26] bg-[#0F0F0F]" data-testid={testid}>
    <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#2D2A26]">
      <div className="flex items-center gap-2">
        <Icon size={12} className="text-[#D92D20]" />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
          {label}
        </span>
      </div>
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">
        {count}
      </span>
    </header>
    <div className="divide-y divide-[#1f1d1a]">{children}</div>
  </section>
);

const EmptyRow = ({ children }) => (
  <div className="px-4 py-8 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-[#757065]">
    {children}
  </div>
);

export const LiveAudiencePanel = ({ token, userToken }) => {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [visitorsData, setVisitorsData] = useState(null);
  const [visitorsTab, setVisitorsTab] = useState("live"); // "live" | "all"
  const [openDossierId, setOpenDossierId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, visitors] = await Promise.all([
        adminLiveAudienceSnapshot(token, userToken),
        adminAudienceVisitors(token, { status: visitorsTab, limit: 60 }, userToken),
      ]);
      setSnap(data);
      setVisitorsData(visitors);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not load live audience snapshot.";
      setError(msg);
      // Only surface a toast for explicit refreshes the user invoked.
    } finally {
      setLoading(false);
    }
  }, [token, userToken, visitorsTab]);

  // Initial + auto-refresh loop
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  const manualRefresh = () => {
    load().then(() => toast.success("Refreshed", { duration: 1000 })).catch(() => {});
  };

  const totals = snap?.totals || {};
  const liveNow = snap?.live_now || [];
  const liveSubs = snap?.live_subscribers || [];
  const recent = snap?.recent_activity || [];
  const heat = snap?.dossier_heat || [];

  const generated = snap?.generated_at;
  const ttlLive = snap?.ttl_seconds?.live ?? 300;
  const ttlEvents = snap?.ttl_seconds?.events ?? 86400;

  const headerStat = useMemo(() => ([
    { label: "Live now",          value: totals.live_now ?? "—",          testid: "live-stat-live-now" },
    { label: "Live subscribers",  value: totals.live_subscribers ?? "—",  testid: "live-stat-live-subs" },
    { label: "Active subs (all)", value: totals.active_subscribers ?? "—", testid: "live-stat-total-subs" },
    { label: "Events 24h",        value: totals.events_24h ?? "—",         testid: "live-stat-events" },
  ]), [totals]);

  return (
    <div data-testid="live-audience-panel">
      {/* Header */}
      <div className="flex items-end justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2">
            // SURVEILLANCE FEED — INTERNAL ONLY
          </div>
          <h1 className="font-display text-4xl text-[#E4DFD3]">Live audience</h1>
          <p className="text-[#A39E93] mt-1 text-sm max-w-2xl">
            Anonymous heartbeats over the last {Math.round(ttlLive / 60)} minutes and an event
            feed retained for {Math.round(ttlEvents / 3600)} hours. No IPs, no keystrokes, no
            form data. Admins are excluded from tracking.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label
            className="flex items-center gap-2 border border-[#454038] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] cursor-pointer"
            data-testid="live-auto-refresh-toggle"
          >
            <input
              type="checkbox"
              className="accent-[#D92D20]"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh 10s
          </label>
          <button
            onClick={manualRefresh}
            data-testid="live-refresh-btn"
            className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* IGNORE MY ACTIVITY — admin self-exclusion status + toggle */}
      <IgnoreMyActivityBanner />

      {error && (
        <div
          className="border border-[#D92D20]/40 bg-[#1a0808] text-[#D92D20] px-4 py-3 mb-4 font-mono text-[10px] uppercase tracking-[0.25em]"
          data-testid="live-audience-error"
        >
          // {error}
        </div>
      )}

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
        {headerStat.map((s) => (
          <div
            key={s.label}
            className="border border-[#2D2A26] bg-[#0F0F0F] px-4 py-3"
            data-testid={s.testid}
          >
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">
              {s.label}
            </div>
            <div className="font-display text-3xl text-[#E4DFD3] mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Two-column grid: live now + live subscribers / heat */}
      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        {/* LIVE NOW — clickable rows open dossier drawer */}
        <SectionShell
          icon={Radio}
          label="Live now"
          count={`${liveNow.length} session${liveNow.length === 1 ? "" : "s"}`}
          testid="section-live-now"
        >
          {liveNow.length === 0 ? (
            <EmptyRow>No one online right now.</EmptyRow>
          ) : (
            liveNow.map((s) => (
              <button
                type="button"
                key={s.session_id}
                onClick={() => setOpenDossierId(s.session_id)}
                className="w-full text-left px-4 py-2.5 grid grid-cols-12 gap-2 items-center text-sm hover:bg-[#1a1816] focus:outline-none focus:bg-[#1a1816]"
                data-testid={`live-now-row-${s.session_id}`}
              >
                <div className="col-span-1">
                  <span className="inline-block w-2 h-2 rounded-full bg-[#5DD4B3] animate-pulse" />
                </div>
                <div className="col-span-6 font-mono text-xs text-[#E4DFD3] truncate">
                  {s.route}
                </div>
                <div className="col-span-3 font-mono text-[10px] text-[#A39E93] truncate">
                  {s.subscriber_email || (
                    <span className="text-[#CBB892] tracking-[0.18em]" data-testid={`live-now-callsign-${s.session_id}`}>{s.callsign || "GHOST-0000"}</span>
                  )}
                </div>
                <div className="col-span-2 text-right">
                  <DeviceTag device={s.device} />
                  <div className="font-mono text-[9px] text-[#757065]">
                    {fmtAgo(s.last_seen)}
                  </div>
                </div>
              </button>
            ))
          )}
        </SectionShell>

        {/* LIVE SUBSCRIBERS */}
        <SectionShell
          icon={Users}
          label="Live subscribers"
          count={`${liveSubs.length} on-site`}
          testid="section-live-subscribers"
        >
          {liveSubs.length === 0 ? (
            <EmptyRow>No known subscribers online right now.</EmptyRow>
          ) : (
            liveSubs.map((s, i) => (
              <div
                key={`${s.email}-${i}`}
                className="px-4 py-2.5 grid grid-cols-12 gap-2 items-center"
                data-testid={`live-subscriber-row-${i}`}
              >
                <div className="col-span-1">
                  <span className="inline-block w-2 h-2 rounded-full bg-[#CBB892]" />
                </div>
                <div className="col-span-6 font-mono text-xs text-[#E4DFD3] truncate">
                  {s.email}
                </div>
                <div className="col-span-3 font-mono text-[10px] text-[#A39E93] truncate">
                  {s.route}
                </div>
                <div className="col-span-2 text-right font-mono text-[9px] text-[#757065]">
                  {fmtAgo(s.last_seen)}
                </div>
              </div>
            ))
          )}
        </SectionShell>
      </div>

      {/* Recent activity + Dossier heat */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* RECENT ACTIVITY (wider) */}
        <div className="lg:col-span-2">
          <SectionShell
            icon={Activity}
            label="Recent activity · 24h"
            count={`${recent.length} event${recent.length === 1 ? "" : "s"}`}
            testid="section-recent-activity"
          >
            {recent.length === 0 ? (
              <EmptyRow>No activity in the last 24 hours.</EmptyRow>
            ) : (
              recent.slice(0, 100).map((e, i) => (
                <div
                  key={`${e.ts}-${i}`}
                  className="px-4 py-2.5 grid grid-cols-12 gap-2 items-center"
                  data-testid={`activity-row-${i}`}
                >
                  <div className="col-span-2">
                    <KindBadge kind={e.kind} />
                  </div>
                  <div className="col-span-5 font-mono text-xs text-[#E4DFD3] truncate">
                    {e.route}
                  </div>
                  <div className="col-span-3 font-mono text-[10px] text-[#A39E93] truncate">
                    {e.subscriber_email || <span className="text-[#CBB892] tracking-[0.18em]">{e.callsign || "GHOST-0000"}</span>}
                  </div>
                  <div className="col-span-2 text-right font-mono text-[9px] text-[#757065]">
                    {fmtAgo(e.ts)}
                  </div>
                </div>
              ))
            )}
          </SectionShell>
        </div>

        {/* DOSSIER HEAT */}
        <SectionShell
          icon={Flame}
          label="Dossier heat · 24h"
          count={`${heat.length}`}
          testid="section-dossier-heat"
        >
          {heat.length === 0 ? (
            <EmptyRow>No dossier traffic yet.</EmptyRow>
          ) : (
            heat.slice(0, 20).map((h, i) => (
              <div
                key={h.dossier_slug || i}
                className="px-4 py-2.5 grid grid-cols-12 gap-2 items-center"
                data-testid={`heat-row-${i}`}
              >
                <div className="col-span-7">
                  <div className="font-mono text-xs text-[#E4DFD3] truncate">
                    {h.title || h.dossier_slug}
                  </div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">
                    {h.dossier_slug}
                  </div>
                </div>
                <div className="col-span-3 text-right">
                  <div className="flex items-center justify-end gap-1 font-mono text-[10px] text-[#D92D20]">
                    <Eye size={10} /> {h.events}
                  </div>
                  <div className="font-mono text-[9px] text-[#757065]">
                    {h.opens} open · {h.source_clicks} src
                  </div>
                </div>
                <div className="col-span-2 text-right font-mono text-[10px] text-[#CBB892]">
                  {h.unique_subscribers} sub
                </div>
              </div>
            ))
          )}
        </SectionShell>
      </div>

      <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mt-6">
        // Snapshot: {generated || "—"} · {loading ? "fetching…" : "idle"}
      </p>

      {/* AUDIENCE DOSSIER — clickable visitor list (live + recent 30d) */}
      <div className="mt-6">
        <SectionShell
          icon={FileText}
          label={`Audience dossier · ${visitorsTab === "live" ? "live" : "last 30d"}`}
          count={
            visitorsData
              ? `${visitorsData.live_count ?? 0} live · ${visitorsData.total ?? 0} total`
              : "—"
          }
          testid="section-audience-dossier"
        >
          <div className="flex items-center gap-2 px-4 py-2 border-b border-[#2D2A26] bg-[#0c0b09]">
            <button
              type="button"
              onClick={() => setVisitorsTab("live")}
              data-testid="dossier-tab-live"
              className={`font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 border ${
                visitorsTab === "live"
                  ? "border-[#D92D20] text-[#D92D20]"
                  : "border-[#454038] text-[#A39E93] hover:border-[#D92D20]"
              }`}
            >
              Live now
            </button>
            <button
              type="button"
              onClick={() => setVisitorsTab("all")}
              data-testid="dossier-tab-all"
              className={`font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 border ${
                visitorsTab === "all"
                  ? "border-[#D92D20] text-[#D92D20]"
                  : "border-[#454038] text-[#A39E93] hover:border-[#D92D20]"
              }`}
            >
              Last 30d
            </button>
            <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] ml-auto">
              click a row to open the case file
            </span>
          </div>

          {(!visitorsData || (visitorsData.rows || []).length === 0) ? (
            <EmptyRow>
              {visitorsTab === "live"
                ? "No live sessions in the last 2 minutes."
                : "No sessions in window."}
            </EmptyRow>
          ) : (
            (visitorsData.rows || []).map((v) => (
              <button
                type="button"
                key={v.session_id}
                onClick={() => setOpenDossierId(v.session_id)}
                className="w-full text-left px-4 py-2.5 grid grid-cols-12 gap-2 items-center hover:bg-[#1a1816] focus:outline-none focus:bg-[#1a1816]"
                data-testid={`dossier-visitor-row-${v.session_id}`}
              >
                <div className="col-span-1 flex items-center">
                  <span
                    className={`inline-block w-2 h-2 rounded-full ${
                      v.is_live ? "bg-[#5DD4B3] animate-pulse" : "bg-[#454038]"
                    }`}
                  />
                </div>
                <div className="col-span-4 font-mono text-xs text-[#E4DFD3] truncate" title={v.last_route}>
                  {v.last_route || "/"}
                </div>
                <div className="col-span-2 font-mono text-[10px] text-[#A39E93] truncate">
                  {v.subscriber_email || <span className="text-[#CBB892] tracking-[0.18em]" data-testid={`dossier-row-callsign-${v.session_id}`}>{v.callsign || "GHOST-0000"}</span>}
                </div>
                <div className="col-span-2 font-mono text-[10px] text-[#CBB892] text-right tabular-nums">
                  <span className="inline-flex items-center gap-1 justify-end">
                    <Eye size={10} className="opacity-60" />{v.event_count}
                  </span>{" "}
                  <span className="inline-flex items-center gap-1 justify-end">
                    <MousePointerClick size={10} className="opacity-60" />
                    {Math.round(v.max_scroll_pct || 0)}%
                  </span>
                </div>
                <div className="col-span-3 text-right">
                  <DeviceTag device={v.device} />
                  <div className="font-mono text-[9px] text-[#757065]">
                    {fmtAgo(v.last_seen)}
                  </div>
                </div>
              </button>
            ))
          )}
        </SectionShell>
      </div>

      <VisitorDossierDrawer
        open={!!openDossierId}
        sessionId={openDossierId}
        token={token}
        userToken={userToken}
        onClose={() => setOpenDossierId(null)}
      />

      <ReachPanel token={token} userToken={userToken} />
    </div>
  );
};

/**
 * Small inline component shown at the top of the Live Audience panel.
 * Tells the admin whether their own activity is being excluded from
 * analytics and provides a quick on/off toggle. Stored client-side
 * (localStorage) so it survives reloads.
 */
const IgnoreMyActivityBanner = () => {
  const [on, setOn] = useState(() => getAdminIgnoreSelf());

  // Listen for cross-tab changes
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "archive_admin_ignore_self") setOn(getAdminIgnoreSelf());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const toggle = () => {
    const next = !on;
    setAdminIgnoreSelf(next);
    setOn(next);
    toast.success(next ? "Your activity is ignored." : "Tracking your activity now.", { duration: 1500 });
  };

  return (
    <div
      data-testid="admin-ignore-banner"
      className={`flex items-center justify-between gap-3 mb-4 px-4 py-2.5 border ${
        on ? "border-[#5DD4B3]/40 bg-[#091513]" : "border-[#E0A526]/40 bg-[#1a1306]"
      }`}
    >
      <div className="flex items-center gap-2">
        <EyeOff size={12} className={on ? "text-[#5DD4B3]" : "text-[#E0A526]"} />
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: on ? "#5DD4B3" : "#E0A526" }}>
            {on ? "Ignore my activity · ON" : "Ignore my activity · OFF"}
          </div>
          <div className="font-mono text-[9.5px] text-[#A39E93] mt-0.5">
            {on
              ? "Your visits, clicks and dwell time are excluded from Live Audience, Audience Dossier and Reach."
              : "Your activity will appear in analytics. Use only while QA-testing tracking — switch back off when done."}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={toggle}
        data-testid="admin-ignore-toggle"
        className={`font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 border ${
          on
            ? "border-[#454038] text-[#A39E93] hover:border-[#E0A526] hover:text-[#E0A526]"
            : "border-[#5DD4B3]/60 text-[#5DD4B3] hover:bg-[#5DD4B3]/10"
        }`}
      >
        {on ? "Disable temporarily" : "Re-enable ignore"}
      </button>
    </div>
  );
};

export default LiveAudiencePanel;
